// @ts-nocheck
// Type checking is disabled here because the JSON payload from Liquid is dynamic.
// Keeping this file plain keeps it easy to follow.

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * <subscription-widget-component>
 *
 * Renders two subscription rows (90-day and 30-day) that stay in sync with the PDP's
 * current variant. When the user picks a row, we mirror its variant id + selling plan
 * into the hidden buy-buttons form so the cart adds the right product.
 *
 * Data comes from a sibling <script data-subscription-data> emitted by Liquid with the
 * shape: { rows: [{ key, productId, planIndex, groups, variants }, ...], pdpProductId }.
 * pdpProductId is the viewed product (so variant updates work on every PDP that uses this block).
 */
class SubscriptionWidgetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const payload = readPayload(this);
    if (!payload) return;

    this.payload = payload;
    this.section = this.closest('.shopify-section');
    this.rows = payload.rows.map((data) => ({
      data,
      el: this.querySelector(`.subscription-widget__option-${data.key}`),
      matched: null,
    }));

    this.abort = new AbortController();
    const { signal } = this.abort;

    // When the PDP variant changes, re-match our subscription rows to it.
    // queueMicrotask waits until product-form.js has updated the buy-buttons input first.
    document.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const productId = event.detail?.data?.productId;
        if (productId && String(productId) !== String(payload.pdpProductId)) return;
        queueMicrotask(() => this.applyPdpVariant(event.detail?.resource));
      },
      { signal }
    );

    // One delegated change listener for the 90/30 radios (they bubble up to this element).
    this.addEventListener(
      'change',
      (event) => {
        if (event.target?.name !== 'selling_plan') return;
        this.syncForm();
        this.highlightActiveRow();
        this.toggleGalleries();
        this.updateGiftInfoData();
      },
      { signal }
    );

    // The media galleries can be re-rendered by the section morph, so watch for DOM changes.
    if (this.section) {
      this.galleryObserver = new MutationObserver(() => this.toggleGalleries());
      this.galleryObserver.observe(this.section, { childList: true, subtree: true });
    }

    this.applyPdpVariant(null);
    this.highlightActiveRow();
    this.toggleGalleries();
    this.updateGiftInfoData();
  }

  // Mirror the gift variant IDs (and discount codes) of the currently selected
  // subscription option into window.giftInfoData so product-form.js's handleSubmit
  // gate can batch-add them as free gifts.
  updateGiftInfoData() {
    if (!window.giftInfoData) return;

    const checked = this.querySelector('[name="selling_plan"]:checked');
    const option = checked?.closest('.subscription-widget__option');
    const cards = option?.querySelectorAll('.pdp-v3-gift-info__card[data-variant-id]') ?? [];
    const variantIds = Array.from(cards)
      .map((card) => card.dataset.variantId)
      .filter(Boolean);

    if (variantIds.length > 0) {
      window.giftInfoData.variantIds = variantIds;
      window.giftInfoData.discountCodes = this.dataset.discountCodes || '';
      window.giftInfoData.blockId = this.id || 'subscription-v3';
    } else {
      window.giftInfoData.variantIds = [];
      window.giftInfoData.discountCodes = '';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.abort?.abort();
    this.galleryObserver?.disconnect();
  }

  // For each row, find the variant with the same options as the current PDP variant
  // and update that row's variant id, selling plan, and prices.
  applyPdpVariant(pdpVariant) {
    const pdp = pdpVariant ?? this.readPdpFromDom();
    if (!pdp) return;

    for (const row of this.rows) {
      const match = row.data.variants.find((v) => variantMatches(v, pdp)) ?? row.data.variants[0];
      if (!match) return;

      row.matched = match;
      row.el?.setAttribute('data-subscription-variant-id', match.id);
      row.el?.querySelector('input[name="selling_plan"]')?.setAttribute('value', match.sellingPlanId);

      this.renderRowPrice(row);
    }

    this.syncForm();
  }

  // Update price / compare-at / discount text on a single row.
  renderRowPrice(row) {
    if (!row.el || !row.matched) return;

    const plan = row.data.groups?.[0]?.selling_plans?.[row.data.planIndex];
    const base = Number(row.matched.price);
    const compareAt = Number(row.matched.compareAtPrice ?? base);
    const { price, discount } = applySellingPlan(base, compareAt, plan);

    const percentOverride = row.data.key === '90' ? this.dataset.savePercentOverride90 : this.dataset.savePercentOverride;
    const priceOverride =
      row.data.key === '90' && this.dataset['strikethroughPriceOverride'];
    const hideCompare = !discount && !priceOverride;

    if(price) {
      setText(row.el, '[data-subscription-price]', money(price));
    }
    if(compareAt) {
      setText(row.el, '[data-subscription-compare-price]', priceOverride ? priceOverride : money(compareAt));
    }
    if(percentOverride) {
    setText(row.el, '[data-subscription-discount-percentage]', percentOverride ? percentOverride + " OFF" : `${Math.floor(discount)}%`);
    }

    row.el.querySelectorAll('[data-subscription-compare-price]').forEach((el) => {
      el.classList.toggle('hidden', hideCompare);
    });
  }

  // Mirror the currently selected subscription into the buy-buttons form inputs.
  syncForm() {
    if (!this.section) return;

    const checked = this.querySelector('[name="selling_plan"]:checked');
    const checkedRow = checked?.closest('.subscription-widget__option');
    const activeRow = this.rows.find((r) => r.el === checkedRow) ?? this.rows[0];

    const sellingPlanInput = this.section.querySelector('.buy-buttons-block form [name="selling_plan"]');
    const intervalInput = this.section.querySelector(
      '.buy-buttons-block form [name="properties[shipping_interval_frequency]"]'
    );
    const variantInput = this.section.querySelector('.buy-buttons-block input[name="id"]');

    if (sellingPlanInput) sellingPlanInput.value = checked?.value ?? '';

    const plans = activeRow?.data.groups?.[0]?.selling_plans ?? [];
    const plan =
      plans.find((p) => String(p.id) === String(checked?.value)) ?? plans[activeRow?.data.planIndex ?? 0];
    if (intervalInput) intervalInput.value = plan?.name ?? '';

    // option2 looks like "30 servings" -- show just the number.
    const servings = activeRow?.matched?.option2?.split(' ')[0];
    if (servings) setText(this, '[data-servings]', servings);

    const variantId = checkedRow?.getAttribute('data-subscription-variant-id');
    if (variantInput && variantId) variantInput.value = variantId;
  }

  highlightActiveRow() {
    this.querySelectorAll('.subscription-widget__option').forEach((el) => {
      el.classList.remove('subscription-widget__option--active');
    });
    this.querySelector('.subscription-widget__radio:checked')
      ?.closest('.subscription-widget__option')
      ?.classList.add('subscription-widget__option--active');
  }

  // Show the media gallery matching the current purchase mode.
  // Prefers data-gallery-mode on the checked radio (set by subscription-option.liquid),
  // falls back to legacy subscribe/buy-once inference for products that don't set it.
  // If no gallery matches (mode key mismatch / merchant misconfiguration),
  // falls back to showing the first gallery so the user never sees an empty PDP.
  toggleGalleries() {
    if (!this.section) return;

    const checked = this.querySelector('[name="selling_plan"]:checked');
    const activeMode = checked?.dataset?.galleryMode
      || (checked?.value ? 'subscribe' : 'buy-once');

    const galleries = Array.from(this.section.querySelectorAll('media-gallery[data-purchase-mode]'));
    if (galleries.length === 0) return;

    let matched = false;
    galleries.forEach((gallery) => {
      const isActive = gallery.dataset.purchaseMode === activeMode;
      gallery.toggleAttribute('hidden', !isActive);
      if (isActive) matched = true;
    });

    if (!matched) {
      console.warn(`[subscription-widget] No gallery matches mode "${activeMode}". Falling back to first gallery. Check alt-tag mode keys against radio data-gallery-mode.`);
      galleries.forEach((gallery, i) => gallery.toggleAttribute('hidden', i !== 0));
    }
  }

  // Fallback: if no PDP variant was passed in, read the current selection from the variant-picker.
  readPdpFromDom() {
    const el = this.section?.querySelector('variant-picker script[type="application/json"]');
    if (!el?.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }
}

// --- Helpers -------------------------------------------------------------

function readPayload(element) {
  const script = element.parentElement?.querySelector('[data-subscription-data]');
  if (!script?.textContent) return null;
  try {
    const payload = JSON.parse(script.textContent);
    return payload?.rows?.length ? payload : null;
  } catch {
    return null;
  }
}

function money(cents) {
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

// A variant matches the PDP when all three option axes match.
// (Shopify exposes them as option1/2/3, but variant-picker JSON sometimes uses option_1/2/3.)
function variantMatches(variant, pdp) {
  return [1, 2, 3].every((i) => {
    const a = String(variant[`option${i}`] ?? '').trim();
    const b = String(pdp[`option${i}`] ?? pdp[`option_${i}`] ?? '').trim();
    return a === b;
  });
}

// Apply a selling-plan price adjustment to the variant's price. Returns the final price + discount %.
// Per Shopify docs, percentage / fixed_amount adjustments reduce the variant's price (not compare-at).
// compareAtPrice is only used as the reference for the "X% OFF" badge when the plan is a fixed amount
// or a flat price (for percentage plans, we show the plan's raw percentage value instead).
function applySellingPlan(basePrice, compareAtPrice, plan) {
  const adjustment = plan?.price_adjustments?.[0];
  if (!adjustment) return { price: basePrice, discount: 0 };

  const value = Number(adjustment.value);

  if (adjustment.value_type === 'percentage') {
    return {
      price: ((100 - value) / 100) * basePrice,
      discount: value,
    };
  }

  let price = basePrice;
  if (adjustment.value_type === 'fixed_amount') price = basePrice - value;
  else if (adjustment.value_type === 'price') price = value;

  const discount = compareAtPrice > 0 ? ((compareAtPrice - price) / compareAtPrice) * 100 : 0;
  return { price, discount };
}

function setText(parent, selector, text) {
  parent.querySelectorAll(selector).forEach((el) => {
    el.textContent = text;
  });
}

if (!customElements.get('subscription-widget-component')) {
  customElements.define('subscription-widget-component', SubscriptionWidgetComponent);
}
